import * as React from 'react'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import { useFormikContext } from 'formik'
import { DRAFT_TERRITORY_ID } from '@user/components/Settings/LeadAssignmentRule/territoryConditionDraft'

import { useData } from './hooks'
import { ZipCode } from './ZipCode'
import { AttributeValue } from './AttributeValue'
import type { ConditionModel } from '../../../typings'

type Props = {
  setZipCodeValid: (value: boolean) => void
  /**
   * Parks the rule and sends the admin to the map to draw (WWM-17508). Absent when the tenant
   * cannot draw territories, in which case only the existing ones can be picked.
   */
  onDrawNewTerritory?: (condition: ConditionModel) => void
  /** Parks the rule and sends them to the map to reshape a territory it already uses. */
  onRedrawTerritory?: (condition: ConditionModel, territoryId: number) => void
}

export function ConditionAttributeValue(props: Props) {
  const { setZipCodeValid, onDrawNewTerritory, onRedrawTerritory } = props
  const { attribute } = useData()
  const { values } = useFormikContext<ConditionModel>()

  // The saved territories picked in this condition. The one drawn on this visit is excluded:
  // it has no id yet, so there is nothing to redraw -- drawing again replaces it.
  const savedPicks = (
    (values.assignmentRuleConditionValues ?? []) as {
      id?: string
      name?: string
    }[]
  ).filter((value) => value?.id && value.id !== DRAFT_TERRITORY_ID)

  if (attribute === 'ZipCode') {
    return (
      <ZipCode
        setZipCodeValid={setZipCodeValid}
        formFieldName="assignmentRuleConditionValues"
      />
    )
  }

  if (attribute === 'Territory') {
    return (
      <Stack>
        <AttributeValue />
        {onDrawNewTerritory && (
          <Stack direction="row" alignItems="center" spacing={1} mt={-1}>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => onDrawNewTerritory(values)}
              data-testid="draw-new-territory"
            >
              Draw a new territory
            </Button>
            {/* Says what the button costs before it is pressed: the rule form goes away for
                a moment and comes back as it was. */}
            <Typography variant="caption" color="text.secondary">
              Takes you to the map; your rule is kept.
            </Typography>
          </Stack>
        )}

        {onRedrawTerritory && savedPicks.length > 0 && (
          // Reshaping an area is a thing admins do far more often than drawing a new one, and
          // the alternative is leaving the rule, finding the territory in the manage panel and
          // remembering which rule sent you. One button per picked territory rather than a
          // single ambiguous "edit": a rule may name several, and nothing here should guess.
          <Stack direction="row" flexWrap="wrap" alignItems="center" gap={0.5}>
            {savedPicks.map((territory) => (
              <Button
                key={territory.id}
                size="small"
                startIcon={<EditIcon fontSize="small" />}
                onClick={() => onRedrawTerritory(values, Number(territory.id))}
                data-testid={`redraw-territory-${territory.id}`}
              >
                Redraw “{territory.name}”
              </Button>
            ))}
          </Stack>
        )}
      </Stack>
    )
  }

  return <AttributeValue />
}
